/**
 * Generates "Logging" SDK scripts for the documentation page.
 *
 * Each script logs an interaction from an external (non-SpecificAI) model into
 * the platform via the task-specific log methods. The base URL is populated with
 * the current platform URL (same as the "Download query file" flow) so the
 * snippet is runnable as-is.
 */

export const LOGGING_LANGUAGES = ['python', 'typescript', 'go'];

export const LOGGING_TASK_TYPES = [
	{ value: 'classification', label: 'Classification' },
	{ value: 'entity_extraction', label: 'Entity Extraction' },
	{ value: 'summarization', label: 'Summarization' },
];

export const LOGGING_FILE_EXTENSIONS = { python: 'py', typescript: 'ts', go: 'go' };

const PYTHON_SCRIPTS = {
	classification: (baseUrl) => `from specific_ai import SpecificAIClient

# Log a classification interaction from an external (non-SpecificAI) model.
# The record is added to an existing Task or used to bootstrap a new one with
# the same task_name and project_name.
client = SpecificAIClient(url="${baseUrl}")

client.log_classification(
    example="Classify this review as Positive or Negative:\\nThis movie was terrible!", # Replace with your own example
    labels=["Negative"], # Replace with your own labels
    task_name="<your-task-name>",
    project_name="<your-project-name>",
    # is_multilabel=True,  # uncomment for multi-label classification
)

print("Logged external interaction to SpecificAI.")
`,
	entity_extraction: (baseUrl) => `from specific_ai import SpecificAIClient

# Log an entity extraction interaction from an external (non-SpecificAI) model.
# The record is added to an existing Task or used to bootstrap a new one with
# the same task_name and project_name.
client = SpecificAIClient(url="${baseUrl}")

client.log_entity_recognition(
    example="Barack Obama was born in Hawaii.", # Replace with your own example
    task_name="<your-task-name>", # Replace with your own task name
    entities=[ # Replace with your own entities
        {"label": "PERSON", "content": "Barack Obama", "start_index": 0},
        {"label": "LOCATION", "content": "Hawaii", "start_index": 25},
    ],
    project_name="<your-project-name>", # Replace with your own project name
)

print("Logged external interaction to SpecificAI.")
`,
	summarization: (baseUrl) => `from specific_ai import SpecificAIClient

# Log a summarization interaction from an external (non-SpecificAI) model.
# The record is added to an existing Task or used to bootstrap a new one with
# the same task_name and project_name.
client = SpecificAIClient(url="${baseUrl}")

client.log_summarization(
    example="Specific AI is an auto-distillation platform for LLMs...", # Replace with your own example
    task_name="<your-task-name>", # Replace with your own task name
    summary="Specific AI auto-distills LLMs.", # Replace with your own summary
    project_name="<your-project-name>", # Replace with your own project name
)

print("Logged external interaction to SpecificAI.")
`,
};

const TYPESCRIPT_SCRIPTS = {
	classification: (baseUrl) => `import { SpecificAIClient } from "specific-ai"

// Log a classification interaction from an external (non-Specific.ai) model.
// The record is added to an existing Task or used to bootstrap a new one with
// the same taskName and projectName.
const client = new SpecificAIClient({ URL: "${baseUrl}" });
const example = "Classify this review as Positive or Negative:\\nThis movie was terrible!" // Replace with your own example
const labels = ["Negative"] // Replace with your own labels
const taskName = "<your-task-name>" // Replace with your own task name
const projectName = "<your-project-name>" // Replace with your own project name
client.logClassification(example, {
    labels: labels,
    taskName: taskName,
    projectName: projectName,
    // isMultilabel: true, // uncomment for multi-label classification
});

console.log("Logged external interaction to Specific.ai.");
`,
	entity_extraction: (baseUrl) => `import { SpecificAIClient } from "specific-ai"

// Log an entity extraction interaction from an external (non-Specific.ai) model.
// The record is added to an existing Task or used to bootstrap a new one with
// the same taskName and projectName.
const client = new SpecificAIClient({ URL: "${baseUrl}" });

client.logEntityRecognition("Barack Obama was born in Hawaii.", {
    entities: [
        { label: "PERSON", content: "Barack Obama", start_index: 0 },
        { label: "LOCATION", content: "Hawaii", start_index: 25 },
    ],
    taskName: "<your-task-name>",
    projectName: "<your-project-name>",
});

console.log("Logged external interaction to Specific.ai.");
`,
	summarization: (baseUrl) => `import { SpecificAIClient } from "specific-ai"

// Log a summarization interaction from an external (non-Specific.ai) model.
// The record is added to an existing Task or used to bootstrap a new one with
// the same taskName and projectName.
const client = new SpecificAIClient({ URL: "${baseUrl}" });

const example = "Specific AI is an auto-distillation platform for LLMs..." // Replace with your own example
const summary = "Specific AI auto-distills LLMs." // Replace with your own summary
const taskName = "<your-task-name>" // Replace with your own task name
const projectName = "<your-project-name>" // Replace with your own project name
client.logSummarization(example, {
    summary: summary,
    taskName: taskName,
    projectName: projectName,
});

console.log("Logged external interaction to Specific.ai.");
`,
};

const GO_SCRIPTS = {
	classification: (baseUrl) => `package main

import (
	"context"
	"log"
	specificai "github.com/marketplace-specificai/go-specific-ai-sdk"
)

func main() {
	client, err := specificai.NewClient(
		specificai.WithBaseURL("${baseUrl}"),
	)
	if err != nil {
		log.Fatal(err)
	}
	defer client.Close()

	example := \`This movie was terrible!\` // Replace with your own example
	labels := []string{"Negative"}	// Replace with your own labels
	taskName := "<your-task-name>"       // Task name in the SpecificAI platform
	projectName := "<your-project-name>" // Project name in the SpecificAI platform

	// The task defaults to single-label; add specificai.WithMultilabel() for
	// multi-label classification.
	if err := client.LogClassification(context.Background(), example, labels, taskName, projectName); err != nil {
		log.Fatal(err)
	}

	log.Println("Logged external interaction to SpecificAI.")
}
`,
	entity_extraction: (baseUrl) => `package main

import (
	"context"
	"log"
	specificai "github.com/marketplace-specificai/go-specific-ai-sdk"
)

func main() {
	client, err := specificai.NewClient(
		specificai.WithBaseURL("${baseUrl}"),
	)
	if err != nil {
		log.Fatal(err)
	}
	defer client.Close()

	example := \`Barack Obama was born in Hawaii.\`
	taskName := "<your-task-name>"       // Task name in the SpecificAI platform
	projectName := "<your-project-name>" // Project name in the SpecificAI platform

	label := "PERSON"
	content := "Barack Obama"
	start := 0
	entities := []inference.Entity{{Label: &label, Content: &content, StartIndex: &start}}

	if err := client.LogEntityRecognition(context.Background(), example, entities, taskName, projectName); err != nil {
		log.Fatal(err)
	}

	log.Println("Logged external interaction to SpecificAI.")
}
`,
	summarization: (baseUrl) => `package main

import (
	"context"
	"log"
	specificai "github.com/marketplace-specificai/go-specific-ai-sdk"
)

func main() {
	client, err := specificai.NewClient(
		specificai.WithBaseURL("${baseUrl}"),
	)
	if err != nil {
		log.Fatal(err)
	}
	defer client.Close()

	example := "Specific AI is an auto-distillation platform for LLMs..." // Replace with your own example
	summary := "Specific AI auto-distills LLMs." // Replace with your own summary
	taskName := "<your-task-name>"       // Task name in the SpecificAI platform
	projectName := "<your-project-name>" // Project name in the SpecificAI platform

	if err := client.LogSummarization(context.Background(), example, summary, taskName, projectName); err != nil {
		log.Fatal(err)
	}

	log.Println("Logged external interaction to SpecificAI.")
}
`,
};

const SCRIPTS_BY_LANGUAGE = {
	python: PYTHON_SCRIPTS,
	typescript: TYPESCRIPT_SCRIPTS,
	go: GO_SCRIPTS,
};

/**
 * Returns the logging script for the given language + task type, with the base
 * URL injected.
 * @param {Object} params
 * @param {string} params.language - 'python' | 'typescript' | 'go'
 * @param {string} params.taskType - 'classification' | 'entity_extraction' | 'summarization'
 * @param {string} params.baseUrl - Platform base URL to inject into the snippet
 * @returns {string}
 */
export const getLoggingScript = ({ language, taskType, baseUrl }) => {
	const byTask = SCRIPTS_BY_LANGUAGE[language] || PYTHON_SCRIPTS;
	const builder = byTask[taskType] || byTask.classification;
	return builder(baseUrl);
};
